import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { YouTubeEmbed } from "./YouTubeEmbed";
import { ImprovedFooterThree } from "./ImprovedFooterThree";
import { FullscreenVideoModal } from "./FullscreenVideoModal";
import { useTranslation } from "react-i18next";

interface ShowData {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  startTime: string;
  duration: string;
  price: number;
  currency: string;
  minimumCharge: number;
  availableLanguages: string[];
  groupSize: string;
  min_participants: number;
  max_participants: number;
  min_charge_participants?: number | null;
  cut_off_days?: number;
  language: string;
  has_booking_conflict: boolean;
  isAvailable?: boolean;
  videos?: string[];
  images?: string[];
  faqs?: Array<{
    id: number;
    question: string | null;
    answer: string | null;
  }>;
  venue_info?: {
    name: string | null;
    description: string | null;
  };
}

interface ModalVariantThreeProps {
  show: ShowData;
  date: Date;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookNow: () => void;
  bookingType?: string;
}

export function ModalVariantThree({
  show,
  date,
  open,
  onOpenChange,
  onBookNow,
  bookingType,
}: ModalVariantThreeProps) {
  const { t } = useTranslation();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden h-[90vh] max-h-[800px] flex flex-col">
          <Tabs defaultValue="info" className="h-full flex flex-col">
            {/* Header with Tabs */}
            <div className="shrink-0 border-b">
              <div className="p-4 pb-4">
                <DialogHeader>
                  <DialogTitle className="text-sm lg:text-base leading-tight">
                    <span>{show.name.length > 25 ? `${show.name.slice(0, 25)}...` : show.name}</span>
                    {show.subtitle && (
                      <>
                        <span> - </span>
                        <span className="text-xs lg:text-sm font-normal text-muted-foreground">
                          {show.subtitle.length > 100 ? `${show.subtitle.slice(0, 100)}...` : show.subtitle}
                        </span>
                      </>
                    )}
                  </DialogTitle>
                </DialogHeader>
              </div>
              <div className="px-4">
                <TabsList className="grid w-full grid-cols-3 h-9 rounded-none border-0 bg-muted/30">
                  <TabsTrigger
                    value="info"
                    className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                  >
                    <span>📋</span>
                    <span>{t("booking.shows.sheet.info")}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="faq"
                    className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                  >
                    <span>❓</span>
                    <span>{t("booking.shows.sheet.faq")}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="venue"
                    className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                  >
                    <span>📍</span>
                    <span>{t("booking.shows.sheet.venue")}</span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Tab Content */}
              <TabsContent value="info" className="mt-0">
                <div className="pr-1">
                  {/* Video from API data */}
                  {show.videos && show.videos.length > 0 ? (
                    <div className="mb-3">
                      <video
                        controls
                        className="aspect-[3/1] w-full rounded-md"
                        poster={
                          show.images && show.images.length > 0
                            ? show.images[0]
                            : undefined
                        }
                      >
                        <source src={show.videos[0]} type="video/mp4" />
                        {t("booking.shows.sheet.videoNotSupported")}
                      </video>
                    </div>
                  ) : show.images && show.images.length > 0 ? (
                    <div className="mb-3">
                      <img
                        src={show.images[0]}
                        alt={show.name}
                        className="aspect-[3/1] w-full rounded-md object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="space-y-2 text-sm text-foreground pb-4">
                    <div className="space-y-1">
                      <h4 className="text-base font-medium text-foreground">
                        🎮 {t("booking.shows.sheet.aboutExperience")}
                      </h4>
                      <div
                        className="text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: show.description }}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="faq" className="mt-0">
                <div className="pr-1">
                  <div className="space-y-3">
                    <h3 className="text-base font-medium">
                      ❓ {t("booking.shows.sheet.frequentlyAskedQuestions")}
                    </h3>
                    <div className="space-y-4">
                      {show.faqs && show.faqs.length > 0 ? (
                        show.faqs.map((faq) => (
                          <div
                            key={faq.id}
                            className="p-3 rounded-lg border bg-muted/30"
                          >
                            <h4 className="text-sm font-semibold mb-2">
                              {faq.question}
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t("booking.shows.sheet.noFaqsAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="venue" className="mt-0">
                <div className="pr-1">
                  <div className="space-y-2">
                    <h3 className="text-base font-medium">
                      📍{" "}
                      {show.venue_info?.name ||
                        t("booking.shows.sheet.venueInformation")}
                    </h3>
                    <div className="space-y-1.5">
                      {show.venue_info?.description ? (
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {show.venue_info.description}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t("booking.shows.sheet.noVenueInfo", {
                            language:
                              show.language === "sv"
                                ? t("booking.shows.sheet.swedish")
                                : t("booking.shows.sheet.english"),
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>

            {/* Sticky Footer */}
            <div className="shrink-0 border-t bg-background p-2.5">
              <ImprovedFooterThree
                date={date}
                startTime={show.startTime}
                price={show.price}
                currency={show.currency}
                language={show.language || "en"}
                participants={`${show.min_participants}-${show.max_participants}`}
                duration={show.duration}
                isSoldOut={show.isAvailable === false}
                onBookNow={onBookNow}
                bookingType={bookingType}
                minChargeParticipants={show.min_charge_participants}
                cutOffDays={show.cut_off_days}
              />
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Video Modal */}
      {show.videos && show.videos.length > 0 && (
        <FullscreenVideoModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoId={show.videos[0]}
          show={show}
          date={date}
          onBookNow={onBookNow}
        />
      )}
    </>
  );
}

// import { Show } from "@/lib/mockData";
// import {
//   Dialog,
//   DialogContent,
//   DialogHeader,
//   DialogTitle,
// } from "@/components/ui/dialog";
// import { Button } from "@/components/ui/button";

// interface ModalVariantThreeProps {
//   show: Show;
//   date: Date;
//   open: boolean;
//   onOpenChange: (open: boolean) => void;
//   onBookNow: () => void;
// }

// export function ModalVariantThree({
//   show,
//   date,
//   open,
//   onOpenChange,
//   onBookNow
// }: ModalVariantThreeProps) {
//   return (
//     <Dialog open={open} onOpenChange={onOpenChange}>
//       <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
//         <DialogHeader>
//           <DialogTitle>{show.name}</DialogTitle>
//         </DialogHeader>

//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           <div className="space-y-4">
//             <img
//               src={show.image}
//               alt={show.name}
//               className="w-full h-64 object-cover rounded-lg"
//             />

//             <div className="grid grid-cols-2 gap-4 text-sm">
//               <div>
//                 <span className="font-medium">Duration:</span> {show.duration}
//               </div>
//               <div>
//                 <span className="font-medium">Group Size:</span> {show.groupSize}
//               </div>
//               <div>
//                 <span className="font-medium">Difficulty:</span> {show.difficulty}
//               </div>
//               <div>
//                 <span className="font-medium">Success Rate:</span> {show.successRate}
//               </div>
//             </div>
//           </div>

//           <div className="space-y-4">
//             <div>
//               <h3 className="font-semibold mb-2">{show.subtitle}</h3>
//               <p className="text-sm text-muted-foreground">{show.description}</p>
//             </div>

//             <div>
//               <h4 className="font-medium mb-2">Features:</h4>
//               <ul className="text-sm space-y-1">
//                 {show.features.map((feature, index) => (
//                   <li key={index} className="flex items-start gap-2">
//                     <span className="text-green-500">✓</span>
//                     {feature}
//                   </li>
//                 ))}
//               </ul>
//             </div>
//           </div>
//         </div>

//         <div className="flex justify-end pt-4 border-t">
//           <Button
//             onClick={onBookNow}
//             disabled={show.status === "soldout"}
//             size="lg"
//           >
//             {show.status === "soldout" ? "Sold Out" : `Book Now - ${show.price} ${show.currency}`}
//           </Button>
//         </div>
//       </DialogContent>
//     </Dialog>
//   );
// }
